import React, { useMemo, useCallback, useState } from "react";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { FiRefreshCw, FiSmartphone } from "react-icons/fi";
import DataTable from "../common/DataTable";
import TablePagination from "../common/TablePagination";
import useAdminResource from "../../hooks/useAdminResource";
import adminUserService from "../../services/adminUserService";
import { buildDeviceColumns } from "./deviceColumns";
import { DeviceDetailModal, ScheduleDetailModal, RoomDetailModal } from "./DetailModals";
import useDebouncedValue from "../../hooks/useDebouncedValue";

const CONNECTIVITY = [
  ["all", "All connectivity"],
  ["online", "Online"],
  ["offline", "Offline"],
];
// ⚠️ Default is "Added", NOT "all" — the Rooms tab counts only added devices
// (that is the canonical scope the backend reports as isAddedScope:"added"), so
// defaulting this list to all+available would make the device total visibly
// disagree with the sum of the room counts. "All" stays available as an
// explicit widening, and the banner below says so when it is chosen.
const ADDED = [
  ["true", "Added"],
  ["false", "Available"],
  ["all", "Added & available"],
];

export default function UserDevicesTab() {
  const { userId, deviceIndex } = useOutletContext();
  const navigate = useNavigate();
  const basePath = `/admin/dash/users/${userId}`;

  // Filters live in the query string so a tab is linkable and survives reload.
  // A param is dropped from the URL only when it equals THIS field's default —
  // "all" is the default for brand/status/room but an explicit widening for
  // isAdded, so a blanket `v === "all" → delete` would silently undo it.
  const DEFAULTS = {
    alias: "",
    brand: "all",
    status: "all",
    roomId: "all",
    isAdded: "true",
  };

  const [searchParams, setSearchParams] = useSearchParams();
  const get = (k) => searchParams.get(k) ?? DEFAULTS[k];

  const alias = get("alias");
  const brand = get("brand");
  const status = get("status");
  const isAdded = get("isAdded");
  const roomId = get("roomId");
  const page = Number(searchParams.get("page") || 1);
  const limit = Number(searchParams.get("limit") || 20);

  const debouncedAlias = useDebouncedValue(alias, 300);

  // Detail opens in a modal rather than pushing a route, so the list, its
  // filters and the scroll position all survive closing it. The three can
  // hand off to each other (device -> its room, device -> a schedule).
  const [detailDevice, setDetailDevice] = useState(null);
  const [detailRoom, setDetailRoom] = useState(null);
  const [detailScheduleId, setDetailScheduleId] = useState(null);

  const patchParams = useCallback(
    (patch) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        for (const [k, v] of Object.entries(patch)) {
          const isDefault =
            v === undefined || v === null || (k in DEFAULTS && String(v) === DEFAULTS[k]);
          if (isDefault) next.delete(k);
          else next.set(k, String(v));
        }
        // Any filter change invalidates the current page number.
        if (!("page" in patch)) next.delete("page");
        return next;
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [setSearchParams]
  );

  const fetcher = useCallback(
    () =>
      adminUserService.fetchUserDevices(userId, {
        page,
        limit,
        alias: debouncedAlias,
        brand,
        status,
        // "unassigned" is a real value the backend understands; don't drop it.
        roomId,
        // "all" is the only case that widens past the added set.
        isAdded: isAdded === "all" ? undefined : isAdded === "true",
        includeRemoved: isAdded === "all" ? true : undefined,
      }),
    [userId, page, limit, debouncedAlias, brand, status, roomId, isAdded]
  );

  const { data, meta, state, error, refetch, dismissError } = useAdminResource(
    fetcher,
    [userId, page, limit, debouncedAlias, brand, status, roomId, isAdded]
  );

  const devices = data || [];
  const columns = useMemo(
    () =>
      buildDeviceColumns({
        navigate,
        basePath,
        onOpenRoom: (room) => setDetailRoom(room),
        // "In N active" scopes the Schedules tab to this device — that IS a
        // navigation (a different list), not a detail, so it stays a route.
        onOpenSchedules: (d) =>
          navigate(
            `${basePath}/schedules?deviceId=${encodeURIComponent(d.deviceId)}`
          ),
      }),
    [navigate, basePath]
  );

  // Brand options come from the data itself — `brand` is a free string on the
  // model (integrations are seeded at runtime), so a hard-coded list would go
  // stale the moment a new provider ships.
  const brandOptions = useMemo(() => {
    const set = new Set((deviceIndex?.all || []).map((d) => d.brand).filter(Boolean));
    return [...set].sort();
  }, [deviceIndex]);

  const roomOptions = useMemo(() => {
    const byId = new Map();
    for (const d of deviceIndex?.all || []) {
      if (d.roomId?._id) byId.set(d.roomId._id, d.roomId.name);
    }
    return [...byId.entries()];
  }, [deviceIndex]);

  // Compare against each field's own default — with isAdded defaulting to
  // "true", a hard-coded `isAdded !== "all"` would report every fresh view as
  // filtered and show "no devices match these filters" on an empty account.
  const isFiltered = Object.keys(DEFAULTS).some(
    (k) => (searchParams.get(k) ?? DEFAULTS[k]) !== DEFAULTS[k]
  );

  const clearFilters = () => setSearchParams(new URLSearchParams());

  const select = "rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none";

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <input
          type="search"
          value={alias}
          onChange={(e) => patchParams({ alias: e.target.value })}
          placeholder="Search device name…"
          className={`${select} min-w-[200px] flex-1`}
        />

        <select value={brand} onChange={(e) => patchParams({ brand: e.target.value })} className={select}>
          <option value="all">All brands</option>
          {brandOptions.map((b) => (
            <option key={b} value={b}>{b}</option>
          ))}
        </select>

        <select value={status} onChange={(e) => patchParams({ status: e.target.value })} className={select}>
          {CONNECTIVITY.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>

        <select value={isAdded} onChange={(e) => patchParams({ isAdded: e.target.value })} className={select}>
          {ADDED.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>

        <select value={roomId} onChange={(e) => patchParams({ roomId: e.target.value })} className={select}>
          <option value="all">All rooms</option>
          <option value="unassigned">Ungrouped</option>
          {roomOptions.map(([id, name]) => (
            <option key={id} value={id}>{name}</option>
          ))}
        </select>

        <button
          type="button"
          onClick={() => refetch()}
          className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          <FiRefreshCw size={14} className={state === "loading" ? "animate-spin" : ""} />
          Refresh
        </button>
      </div>

      <DataTable
        columns={columns}
        data={devices}
        state={state}
        error={error}
        onRetry={() => refetch()}
        onDismissError={dismissError}
        getRowId={(row) => row._id}
        onRowClick={(row) => setDetailDevice(row)}
        emptyIcon={FiSmartphone}
        emptyTitle={isFiltered ? "No devices match these filters" : "No devices yet"}
        emptyMessage={
          isFiltered
            ? "Try widening the filters above."
            : "This user has not connected any devices."
        }
        isFiltered={isFiltered}
        onClearFilters={clearFilters}
      />

      <TablePagination
        page={meta.page}
        totalPages={meta.totalPages}
        total={meta.total}
        limit={meta.limit}
        onPageChange={(p) => patchParams({ page: p })}
        onLimitChange={(l) => patchParams({ limit: l, page: 1 })}
      />

      <DeviceDetailModal
        open={Boolean(detailDevice)}
        onClose={() => setDetailDevice(null)}
        userId={userId}
        device={detailDevice}
        onOpenRoom={(room) => { setDetailDevice(null); setDetailRoom(room); }}
        onOpenSchedule={(row) => {
          setDetailDevice(null);
          setDetailScheduleId(String(row.scheduleId ?? row._id));
        }}
      />

      <RoomDetailModal
        open={Boolean(detailRoom)}
        onClose={() => setDetailRoom(null)}
        userId={userId}
        room={detailRoom}
        onOpenDevice={(d) => { setDetailRoom(null); setDetailDevice(d); }}
      />

      <ScheduleDetailModal
        open={Boolean(detailScheduleId)}
        onClose={() => setDetailScheduleId(null)}
        userId={userId}
        scheduleId={detailScheduleId}
        deviceIndex={deviceIndex}
      />
    </div>
  );
}
